import React, { useState } from "react";
import { Box, Divider, MenuItem, Select, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { colorsAndLabelsByOp, getAllLeafOpInfo } from "system/query/QueryUtility";
import { Edit } from "@mui/icons-material";
import { SimpleFormLabel } from "components/dialogs/SimpleFormDialog";
import ConfirmationDialog from "components/dialogs/ConfirmationDialog";

export interface QueryLeafNodeOpSelectorProps {
  op: string;
  setOp: (op: string) => void;
  inSpan?: boolean;
  allowedFieldOps?: string[];
}

export default function QueryLeafNodeOpSelector(props: QueryLeafNodeOpSelectorProps): JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const { op, setOp, allowedFieldOps } = props;

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [lastOp, setLastOp] = useState<string | undefined>(undefined);

  const allOpOptions = getAllLeafOpInfo(i18n, theme.colors.namedColors);
  const allowedOpOptions = allowedFieldOps ? allOpOptions.filter(op => allowedFieldOps.includes(op.op)) : allOpOptions;
  const disabledOpOptions = allowedFieldOps ? allOpOptions.filter(op => !allowedFieldOps.includes(op.op)) : [];
  const currentOp = colorsAndLabelsByOp(i18n, theme.colors.namedColors, op);

  function preSetOp(newOp: string) {
    if (newOp === op) {
      setLastOp(undefined);
      setIsEditing(false);
    } else if (!allowedFieldOps || allowedFieldOps.includes(newOp)) {
      setOp(newOp);
      setLastOp(undefined);
      setIsEditing(false);
    } else {
      setLastOp(newOp);
    }
  }

  return (
    <Box sx={{ mb: 1 }}>
      {!isEditing &&
        <SimpleFormLabel>
         {currentOp.label}:
         {!props.inSpan &&
          <Edit
            sx={{ pl: 1, fontSize: "1.5em", verticalAlign: "bottom", cursor: "pointer" }}
            onClick={() => setIsEditing(true)}
          />
         }
        </SimpleFormLabel>
      }
      {isEditing &&
        <>
        <SimpleFormLabel>{i18n.t("query-form.operator")}:</SimpleFormLabel>
        <Select
          value={op}
          onChange={(e) => preSetOp(e.target.value as string)}
          sx={{ width: "100%" }}
        >
          {allowedOpOptions.map(op => (
            <MenuItem value={op.op} key={op.op} >
              {op.label}
            </MenuItem>
          ))}
          {disabledOpOptions.length > 0 &&
            <Divider />
          }
          {disabledOpOptions.map(op => (
            <MenuItem value={op.op} key={op.op}>
              <span style={{ color: theme.palette.text.disabled }}>{op.label}</span>
            </MenuItem>
          ))}
        </Select>
        </>
      }
      <ConfirmationDialog
        isOpen={!!lastOp}
        title={i18n.t("query-form.incompatible-operator")}
        description={i18n.t("query-form.incompatible-operator-change-warning")}
        onClose={(confirmed) => {
          if (confirmed) {
            if (lastOp) {
              setOp(lastOp);
            }
            setIsEditing(false);
          }
          setLastOp(undefined);
        }}
        />
    </Box>
  );
}